/**
 * Dashboard page
 * Main overview page with metrics and recent activity
 */

import { ReactElement, useEffect, useState } from "react";
import styles from "./DashboardPage.module.css";
import { Icon } from "@/components/ui/Icon";
import { formatCurrency } from "@/utils";
import { workOrdersService } from "@/services/workOrdersService";
import { materialsService } from "@/services/materialsService";
import { reportsService } from "@/services/reportsService";

interface DashboardPageProps {
  onNavigate: (path: string) => void;
}

interface Metric {
  id: string;
  label: string;
  value: string | number;
  icon: string;
  trend?: {
    direction: "up" | "down";
    value: string;
  };
  variant?: "primary" | "warning" | "danger" | "success";
  subtext?: string;
}

interface WorkOrder {
  id: string;
  client: string;
  technician: {
    name: string;
    initials: string;
  };
  status: "pending" | "inProgress" | "completed";
  date: string;
}

interface InventoryItem {
  id: string;
  name: string;
  category: string;
  quantity: number;
  unit: string;
  value: number;
  percentage: number;
}

const getStatusLabel = (status: WorkOrder["status"]): string => {
  const labels = {
    pending: "Pendiente",
    inProgress: "En Progreso",
    completed: "Completado",
  };
  return labels[status];
};

const getStockLevel = (percentage: number): "low" | "medium" | "high" => {
  if (percentage <= 30) return "low";
  if (percentage <= 60) return "medium";
  return "high";
};

const DEFAULT_METRICS: Metric[] = [
  {
    id: "active-services",
    label: "Servicios Activos",
    value: 0,
    icon: "checkCircle",
    trend: { direction: "up", value: "+0%" },
    variant: "primary",
  },
  {
    id: "pending-quotes",
    label: "Presupuestos Pendientes",
    value: 0,
    icon: "document",
    trend: { direction: "down", value: "-0%" },
    variant: "warning",
  },
  {
    id: "stock-alerts",
    label: "Alertas de Stock",
    value: "0 Críticos",
    icon: "alertTriangle",
    variant: "danger",
    subtext: "ADVERTENCIA",
  },
  {
    id: "monthly-revenue",
    label: "Ingresos Mensuales",
    value: "-",
    icon: "dollarSign",
    variant: "success",
  },
];

/**
 * Dashboard page component
 */
export function DashboardPage({
  onNavigate,
}: DashboardPageProps): ReactElement {
  const [metrics, setMetrics] = useState<Metric[]>(DEFAULT_METRICS);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);

        const [dashboardRes, matRes, woRecentRes] = await Promise.all([
          reportsService.getDashboard(),
          materialsService.list({ page_size: 100 }),
          workOrdersService.list({ page_size: 5 }),
        ]);

        const dashboardKpis = dashboardRes.kpis;

        setMetrics([
          {
            id: "active-services",
            label: "Servicios Activos",
            value: dashboardKpis.activeServices,
            icon: "checkCircle",
            trend: { direction: "up", value: "+0%" },
            variant: "primary",
          },
          {
            id: "pending-quotes",
            label: "Presupuestos Pendientes",
            value: dashboardKpis.pendingQuotes,
            icon: "document",
            trend: { direction: "down", value: "-0%" },
            variant: "warning",
          },
          {
            id: "stock-alerts",
            label: "Alertas de Stock",
            value: `${dashboardKpis.stockAlerts} Críticos`,
            icon: "alertTriangle",
            variant: "danger",
            subtext: "ADVERTENCIA",
          },
          {
            id: "monthly-revenue",
            label: "Ingresos Mensuales",
            value: dashboardKpis.monthlyRevenue,
            icon: "dollarSign",
            variant: "success",
          },
        ]);

        // Work orders — map API status to local status
        const woStatusMap: Record<string, WorkOrder["status"]> = {
          pending: "pending",
          assigned: "pending",
          in_progress: "inProgress",
          completed: "completed",
          cancelled: "completed",
        };
        const mappedOrders: WorkOrder[] = woRecentRes.results.map((o) => {
          const dateStr = o.scheduled_date ?? "";
          const date = dateStr ? new Date(dateStr) : new Date();
          const formattedDate = new Intl.DateTimeFormat("es-ES", {
            day: "2-digit",
            month: "short",
          }).format(date);
          return {
            id: o.order_number,
            client: o.client.name,
            technician: {
              name: o.assigned_technician?.name ?? "Sin asignar",
              initials: o.assigned_technician?.name
                ? o.assigned_technician.name
                    .split(/[.@\s]/)
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((s) => s[0].toUpperCase())
                    .join("")
                : "SA",
            },
            status: woStatusMap[o.status] ?? "pending",
            date: formattedDate,
          };
        });
        setWorkOrders(mappedOrders);

        // Inventory items — first 4
        const mappedItems: InventoryItem[] = matRes.results
          .slice(0, 4)
          .map((m) => {
            const qty = m.quantity ?? 0;
            const minStock = m.minimum_stock ?? 1;
            const pct = Math.min(100, Math.round((qty / minStock) * 100));
            const pricePerUnit = parseFloat(m.price_per_unit ?? "0");
            return {
              id: String(m.id),
              name: m.name,
              category: m.category,
              quantity: qty,
              unit: m.unit ?? "unidades",
              value: pricePerUnit * qty,
              percentage: pct,
            };
          });
        setInventoryItems(mappedItems);
      } catch (err) {
        console.error("Dashboard load error:", err);
      } finally {
        setIsLoading(false);
      }
    };
    void load();
  }, []);

  return (
    <div className={styles.dashboard}>
      {/* Metrics */}
      <div className={styles.metrics}>
        {metrics.map((metric) => (
          <div key={metric.id} className={styles.metricCard}>
            <div className={styles.metricHeader}>
              <div
                className={`${styles.metricIcon} ${styles[metric.variant || "primary"]}`}
              >
                <Icon name={metric.icon} size={24} />
              </div>
              {metric.trend && (
                <div
                  className={`${styles.metricTrend} ${styles[metric.trend.direction]}`}
                >
                  {metric.trend.direction === "up" ? "↑" : "↓"}{" "}
                  {metric.trend.value}
                </div>
              )}
            </div>
            <p className={styles.metricLabel}>{metric.label}</p>
            <h3
              className={`${styles.metricValue} ${metric.variant === "danger" ? styles.warning : ""}`}
            >
              {metric.id === "monthly-revenue" &&
              typeof metric.value === "number"
                ? formatCurrency(metric.value)
                : metric.value}
            </h3>
            {metric.subtext && (
              <p className={styles.metricSubtext}>{metric.subtext}</p>
            )}
          </div>
        ))}
      </div>

      {/* Sections */}
      <div className={styles.sections}>
        {/* Recent Work Orders */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <div>
              <h3 className={styles.sectionTitle}>
                Órdenes de Trabajo Recientes
              </h3>
              <p className={styles.sectionSubtitle}>Últimas de tus técnicos</p>
            </div>
            <button
              className={styles.viewAllLink}
              onClick={() => onNavigate("/workorders")}
            >
              Ver todas →
            </button>
          </div>

          <div className={styles.tableContainer}>
            {isLoading ? (
              <p style={{ padding: "1rem" }}>Cargando...</p>
            ) : (
              <table className={styles.table}>
                <thead className={styles.tableHeader}>
                  <tr>
                    <th>ID</th>
                    <th>CLIENTE</th>
                    <th>TÉCNICO</th>
                    <th>ESTADO</th>
                    <th>FECHA</th>
                  </tr>
                </thead>
                <tbody className={styles.tableBody}>
                  {workOrders.map((order) => (
                    <tr key={order.id}>
                      <td className={styles.workOrderId}>{order.id}</td>
                      <td>{order.client}</td>
                      <td>
                        <div className={styles.technicianBadge}>
                          <div className={styles.technicianAvatar}>
                            {order.technician.initials}
                          </div>
                          {order.technician.name}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`${styles.statusBadge} ${styles[order.status]}`}
                        >
                          {getStatusLabel(order.status)}
                        </span>
                      </td>
                      <td>{order.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {/* Inventory Levels */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <div>
              <h3 className={styles.sectionTitle}>Niveles de Inventario</h3>
              <p className={styles.sectionSubtitle}>
                Top 4 productos más utilizados
              </p>
            </div>
          </div>

          <div className={styles.inventoryList}>
            {isLoading ? (
              <p style={{ padding: "1rem" }}>Cargando...</p>
            ) : (
              inventoryItems.map((item) => (
                <div key={item.id} className={styles.inventoryItem}>
                  <div className={styles.inventoryInfo}>
                    <h4 className={styles.inventoryName}>{item.name}</h4>
                    <p className={styles.inventoryCategory}>{item.category}</p>
                    <div className={styles.inventoryBar}>
                      <div
                        className={`${styles.inventoryProgress} ${styles[getStockLevel(item.percentage)]}`}
                        style={{ width: `${item.percentage}%` }}
                      />
                    </div>
                  </div>
                  <div className={styles.inventoryStock}>
                    <p className={styles.inventoryQuantity}>
                      {item.quantity}
                      <span
                        style={{
                          fontSize: "12px",
                          fontWeight: "normal",
                          marginLeft: "4px",
                        }}
                      >
                        {item.unit}
                      </span>
                    </p>
                    <p className={styles.inventoryValue}>
                      {formatCurrency(item.value)}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default DashboardPage;
